import { useEffect, useState, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ChefHat, Check, X, Printer, Package, Clock, MapPin, Phone,
  DollarSign, TrendingUp, Home, Power, CheckCircle2, Bike
} from "lucide-react";
import { toast } from "sonner";

export default function Restaurant() {
  const { user, loading, logout } = useAuth();
  const { lang } = useLang();
  const [tab, setTab] = useState("new");
  const [newOrders, setNewOrders] = useState([]);
  const [active, setActive] = useState([]);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState({});
  const [restaurant, setRestaurant] = useState(null);

  const loadAll = async () => {
    try {
      const [r, n, a, h, s] = await Promise.all([
        api.get("/restaurant/me"),
        api.get("/restaurant/orders/new"),
        api.get("/restaurant/orders/active"),
        api.get("/restaurant/orders/history"),
        api.get("/restaurant/stats"),
      ]);
      setRestaurant(r.data);
      setNewOrders(n.data);
      setActive(a.data);
      setHistory(h.data);
      setStats(s.data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (!user?.is_restaurant_owner) return undefined;
    const t = setTimeout(loadAll, 0);
    const iv = setInterval(loadAll, 15000);
    return () => { clearTimeout(t); clearInterval(iv); };
  }, [user]);

  if (loading) return <div className="p-8 text-center">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_restaurant_owner) return <Navigate to="/" replace />;

  const accept = async (id) => {
    try {
      await api.post(`/restaurant/orders/${id}/accept`);
      toast.success(lang === "ar" ? "تم قبول الطلب" : "Order accepted");
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const reject = async (id) => {
    if (!window.confirm(lang === "ar" ? "هل أنت متأكد من رفض هذا الطلب؟" : "Reject this order?")) return;
    try {
      await api.post(`/restaurant/orders/${id}/reject`, { reason: "rejected_by_restaurant" });
      toast.success(lang === "ar" ? "تم رفض الطلب" : "Order rejected");
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const markReady = async (id) => {
    try {
      await api.post(`/restaurant/orders/${id}/ready`);
      toast.success(lang === "ar" ? "الطلب جاهز للاستلام" : "Order ready");
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const currentList = tab === "new" ? newOrders : tab === "active" ? active : history;

  return (
    <div className="bg-[#F7F7F7] min-h-screen" dir={lang === "ar" ? "rtl" : "ltr"}>
      {/* Header */}
      <div className="bg-white border-b border-[#EAEAEA] sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#E05D3A] flex items-center justify-center flex-shrink-0">
            <ChefHat className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-[#6B6B6B]">{lang === "ar" ? "لوحة المطعم" : "Restaurant dashboard"}</p>
            <p className="font-bold text-[15px] text-[#1A1A1A] truncate" data-testid="rest-name">
              {restaurant ? (lang === "ar" ? restaurant.name_ar : restaurant.name_en) : user.name}
            </p>
          </div>
          <button
            onClick={logout}
            className="w-9 h-9 rounded-full hover:bg-[#F5F5F5] flex items-center justify-center"
            data-testid="rest-logout"
          >
            <Power className="w-4 h-4 text-[#E05D3A]" />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-3xl mx-auto px-4 py-4 grid grid-cols-3 gap-3">
        <StatCard
          icon={<Clock className="w-4 h-4" />}
          label={lang === "ar" ? "قيد الانتظار" : "Pending"}
          value={stats.pending || 0}
          color="#E05D3A"
        />
        <StatCard
          icon={<Package className="w-4 h-4" />}
          label={lang === "ar" ? "اليوم" : "Today"}
          value={stats.today_orders || 0}
          color="#1A1A1A"
        />
        <StatCard
          icon={<TrendingUp className="w-4 h-4" />}
          label={lang === "ar" ? "الإيرادات" : "Revenue"}
          value={`${(stats.total_revenue || 0).toFixed(0)} ر`}
          color="#00A85A"
        />
      </div>

      {/* Tabs */}
      <div className="max-w-3xl mx-auto px-4">
        <div className="flex gap-2 bg-white p-1.5 rounded-full border border-[#EAEAEA]">
          <TabBtn
            active={tab === "new"}
            onClick={() => setTab("new")}
            label={lang === "ar" ? `جديد (${newOrders.length})` : `New (${newOrders.length})`}
            testId="tab-new"
            highlight={newOrders.length > 0}
          />
          <TabBtn
            active={tab === "active"}
            onClick={() => setTab("active")}
            label={lang === "ar" ? "قيد التحضير" : "Preparing"}
            testId="tab-active"
          />
          <TabBtn
            active={tab === "history"}
            onClick={() => setTab("history")}
            label={lang === "ar" ? "السجل" : "History"}
            testId="tab-history"
          />
        </div>
      </div>

      {/* Orders list */}
      <div className="max-w-3xl mx-auto px-4 py-4 space-y-3 pb-28">
        {currentList.length === 0 ? (
          <div className="bg-white rounded-2xl border border-[#EAEAEA] p-10 text-center" data-testid="empty-state">
            <Home className="w-8 h-8 text-[#BDBDBD] mx-auto mb-2" />
            <p className="text-[#6B6B6B] text-sm">
              {tab === "new"
                ? (lang === "ar" ? "لا توجد طلبات جديدة" : "No new orders")
                : tab === "active"
                ? (lang === "ar" ? "لا توجد طلبات قيد التحضير" : "No active orders")
                : (lang === "ar" ? "لا يوجد سجل بعد" : "No history yet")}
            </p>
          </div>
        ) : (
          currentList.map((order) => (
            <OrderCard
              key={order.order_id}
              order={order}
              lang={lang}
              tab={tab}
              restaurant={restaurant}
              onAccept={accept}
              onReject={reject}
              onMarkReady={markReady}
            />
          ))
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  return (
    <div className="bg-white rounded-2xl border border-[#EAEAEA] p-3">
      <div className="flex items-center gap-1.5" style={{ color }}>
        {icon}
        <span className="text-[11px] font-semibold text-[#6B6B6B]">{label}</span>
      </div>
      <p className="font-black text-[20px] mt-1 text-[#1A1A1A]">{value}</p>
    </div>
  );
}

function TabBtn({ active, onClick, label, testId, highlight }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 py-2 rounded-full text-[13px] font-semibold transition relative ${active ? "bg-[#E05D3A] text-white" : "text-[#1A1A1A] hover:bg-[#F5F5F5]"}`}
      data-testid={testId}
    >
      {label}
      {highlight && !active && <span className="absolute top-0.5 end-2 w-2 h-2 rounded-full bg-[#E05D3A] animate-pulse" />}
    </button>
  );
}

function OrderCard({ order, lang, tab, restaurant, onAccept, onReject, onMarkReady }) {
  const itemCount = (order.items || []).reduce((s, i) => s + (i.quantity || 0), 0);
  const shortId = (order.order_id || "").slice(-6).toUpperCase();
  const timeStr = useMemo(() => {
    try { return new Date(order.created_at).toLocaleTimeString(lang === "ar" ? "ar-SA" : "en-US", { hour: "2-digit", minute: "2-digit" }); }
    catch { return ""; }
  }, [order.created_at, lang]);

  const statusLabel =
    order.status === "placed" ? (lang === "ar" ? "جديد" : "New") :
    order.status === "preparing" ? (lang === "ar" ? "قيد التحضير" : "Preparing") :
    order.status === "on_the_way" ? (lang === "ar" ? "مع المندوب" : "With driver") :
    order.status === "delivered" ? (lang === "ar" ? "تم التوصيل" : "Delivered") :
    order.status === "cancelled" ? (lang === "ar" ? "ملغى" : "Cancelled") :
    order.status;

  const statusColor =
    order.status === "placed" ? "bg-[#FFF1EA] text-[#E05D3A]" :
    order.status === "preparing" ? "bg-[#E8F7EF] text-[#00A85A]" :
    order.status === "on_the_way" ? "bg-[#FFF8E1] text-[#B8860B]" :
    order.status === "delivered" ? "bg-[#F0F0F0] text-[#1A1A1A]" :
    "bg-red-50 text-red-600";

  const printInvoice = () => {
    printOrderInvoice(order, restaurant, lang);
  };

  return (
    <div className="bg-white rounded-2xl border border-[#EAEAEA] overflow-hidden" data-testid={`order-${order.order_id}`}>
      {/* Header row */}
      <div className="p-4 border-b border-[#F0F0F0] flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#FFF1EA] flex items-center justify-center flex-shrink-0">
          <Package className="w-5 h-5 text-[#E05D3A]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-bold text-[14px] text-[#1A1A1A]">
              #{shortId}
            </p>
            <Badge className={`${statusColor} border-0 text-[10px] px-2 py-0 font-bold`}>
              {statusLabel}
            </Badge>
          </div>
          <p className="text-[12px] text-[#6B6B6B] mt-0.5">
            {timeStr} · {itemCount} {lang === "ar" ? "أصناف" : "items"}
          </p>
        </div>
        <p className="font-black text-[16px] text-[#1A1A1A] whitespace-nowrap">
          {order.total?.toFixed(2)} <span className="text-[11px] text-[#6B6B6B]">ر.س</span>
        </p>
      </div>

      {/* Items list */}
      <div className="px-4 py-3 space-y-1.5 max-h-48 overflow-y-auto">
        {(order.items || []).map((i, idx) => (
          <div key={i.menu_item_id || `${i.name_en || i.name_ar}-${idx}`} className="flex items-center justify-between text-[13px]">
            <span className="text-[#1A1A1A] truncate flex-1">
              <span className="font-bold text-[#E05D3A] mx-1">{i.quantity}×</span>
              {lang === "ar" ? i.name_ar : i.name_en}
            </span>
            <span className="text-[#6B6B6B] whitespace-nowrap ms-2">
              {((i.quantity || 1) * (i.price || 0)).toFixed(2)}
            </span>
          </div>
        ))}
      </div>

      {/* Address */}
      {order.address && (
        <div className="px-4 py-2 bg-[#FAFAFA] border-t border-[#F0F0F0] flex items-start gap-2">
          <MapPin className="w-3.5 h-3.5 text-[#E05D3A] flex-shrink-0 mt-0.5" />
          <p className="text-[12px] text-[#6B6B6B] flex-1">
            <span className="font-semibold text-[#1A1A1A]">{order.address.label}</span> · {order.address.street}, {order.address.city}
            {order.address.notes && <span className="block text-[11px] mt-0.5">📝 {order.address.notes}</span>}
          </p>
        </div>
      )}

      {/* Driver / payment info for active orders */}
      {tab !== "new" && (order.driver_name || order.payment_method) && (
        <div className="px-4 py-2 bg-[#FAFAFA] border-t border-[#F0F0F0] flex items-center gap-4 text-[11.5px] text-[#6B6B6B]">
          {order.driver_name && (
            <span className="flex items-center gap-1"><Bike className="w-3 h-3" />{order.driver_name}</span>
          )}
          {order.driver_phone && (
            <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{order.driver_phone}</span>
          )}
          {order.payment_method && (
            <span className="flex items-center gap-1">
              <DollarSign className="w-3 h-3" />
              {order.payment_method === "cash" ? (lang === "ar" ? "نقداً" : "Cash") : (lang === "ar" ? "بطاقة" : "Card")}
            </span>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="p-3 flex gap-2 border-t border-[#F0F0F0]">
        <Button
          variant="outline"
          onClick={printInvoice}
          className="flex-shrink-0 rounded-full h-10 px-3 border-[#EAEAEA]"
          data-testid={`print-${order.order_id}`}
        >
          <Printer className="w-4 h-4 me-1" />
          <span className="text-[12px]">{lang === "ar" ? "طباعة" : "Print"}</span>
        </Button>

        {tab === "new" && (
          <>
            <Button
              variant="outline"
              onClick={() => onReject(order.order_id)}
              className="flex-1 rounded-full h-10 border-red-200 text-red-600 hover:bg-red-50"
              data-testid={`reject-${order.order_id}`}
            >
              <X className="w-4 h-4 me-1" />
              {lang === "ar" ? "رفض" : "Reject"}
            </Button>
            <Button
              onClick={() => onAccept(order.order_id)}
              className="flex-1 rounded-full h-10 bg-[#00A85A] hover:bg-[#008f4d] text-white"
              data-testid={`accept-${order.order_id}`}
            >
              <Check className="w-4 h-4 me-1" />
              {lang === "ar" ? "قبول" : "Accept"}
            </Button>
          </>
        )}

        {tab === "active" && order.status === "preparing" && !order.ready_for_pickup_at && (
          <Button
            onClick={() => onMarkReady(order.order_id)}
            className="flex-1 rounded-full h-10 bg-[#E05D3A] hover:bg-[#C24B2C] text-white"
            data-testid={`ready-${order.order_id}`}
          >
            <CheckCircle2 className="w-4 h-4 me-1" />
            {lang === "ar" ? "جاهز للاستلام" : "Mark ready"}
          </Button>
        )}

        {tab === "active" && order.ready_for_pickup_at && (
          <div className="flex-1 flex items-center justify-center rounded-full h-10 bg-[#E8F7EF] text-[#00A85A] text-[12px] font-bold">
            <CheckCircle2 className="w-4 h-4 me-1" />
            {lang === "ar" ? "جاهز · بانتظار المندوب" : "Ready · waiting for driver"}
          </div>
        )}
      </div>
    </div>
  );
}

// --- Invoice printing (opens a clean printable window) ---
function printOrderInvoice(order, restaurant, lang) {
  // Escape HTML to prevent XSS via user-supplied content (notes, address, item names)
  const esc = (s) => String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

  const shortId = (order.order_id || "").slice(-8).toUpperCase();
  const dt = new Date(order.created_at);
  const dateStr = dt.toLocaleString(lang === "ar" ? "ar-SA" : "en-US");
  const restName = restaurant ? (lang === "ar" ? restaurant.name_ar : restaurant.name_en) : "";
  const itemsHtml = (order.items || [])
    .map((i) => `
      <tr>
        <td>${esc(lang === "ar" ? i.name_ar : i.name_en)}</td>
        <td style="text-align:center">${esc(i.quantity)}</td>
        <td style="text-align:end">${Number(i.price).toFixed(2)}</td>
        <td style="text-align:end">${(i.quantity * i.price).toFixed(2)}</td>
      </tr>
    `)
    .join("");

  const T = {
    title: lang === "ar" ? "فاتورة" : "Invoice",
    order: lang === "ar" ? "طلب رقم" : "Order #",
    date: lang === "ar" ? "التاريخ" : "Date",
    customer: lang === "ar" ? "العميل" : "Customer",
    address: lang === "ar" ? "العنوان" : "Address",
    item: lang === "ar" ? "الصنف" : "Item",
    qty: lang === "ar" ? "الكمية" : "Qty",
    price: lang === "ar" ? "السعر" : "Price",
    total: lang === "ar" ? "المجموع" : "Total",
    subtotal: lang === "ar" ? "الإجمالي الفرعي" : "Subtotal",
    delivery: lang === "ar" ? "رسوم التوصيل" : "Delivery",
    grandTotal: lang === "ar" ? "الإجمالي النهائي" : "Grand Total",
    payment: lang === "ar" ? "طريقة الدفع" : "Payment",
    cash: lang === "ar" ? "نقداً" : "Cash",
    card: lang === "ar" ? "بطاقة" : "Card",
    thankYou: lang === "ar" ? "شكراً لكم — جايك" : "Thank you — Jayek",
    notes: lang === "ar" ? "ملاحظات" : "Notes",
  };

  const html = `
<!doctype html>
<html dir="${lang === "ar" ? "rtl" : "ltr"}" lang="${lang}">
<head>
  <meta charset="utf-8">
  <title>${T.title} · #${shortId}</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Noto Sans Arabic", Arial, sans-serif; margin: 0; padding: 16px; color: #111; }
    .wrap { max-width: 360px; margin: 0 auto; }
    .center { text-align: center; }
    .brand { font-size: 22px; font-weight: 900; color: #E05D3A; letter-spacing: 1px; }
    .rest { font-size: 16px; font-weight: 700; margin-top: 4px; }
    .hr { border: 0; border-top: 1px dashed #888; margin: 12px 0; }
    table { width: 100%; border-collapse: collapse; font-size: 13px; }
    th, td { padding: 6px 4px; border-bottom: 1px dotted #CCC; }
    th { text-align: start; font-weight: 700; }
    .total-row td { font-size: 15px; font-weight: 800; border-top: 2px solid #111; border-bottom: 0; padding-top: 8px; }
    .muted { color: #555; font-size: 12px; margin: 4px 0; }
    @media print {
      body { padding: 0; }
      .no-print { display: none; }
    }
  </style>
</head>
<body>
  <div class="wrap">
    <div class="center brand">جايك · Jayek</div>
    <div class="center rest">${esc(restName)}</div>
    <hr class="hr">
    <div class="muted"><b>${T.order}:</b> #${esc(shortId)}</div>
    <div class="muted"><b>${T.date}:</b> ${esc(dateStr)}</div>
    ${order.address ? `<div class="muted"><b>${T.address}:</b> ${esc([order.address.label, order.address.street, order.address.city].filter(Boolean).join("، "))}</div>` : ""}
    ${order.notes ? `<div class="muted"><b>${T.notes}:</b> ${esc(order.notes)}</div>` : ""}
    <hr class="hr">
    <table>
      <thead>
        <tr>
          <th>${T.item}</th>
          <th style="text-align:center">${T.qty}</th>
          <th style="text-align:end">${T.price}</th>
          <th style="text-align:end">${T.total}</th>
        </tr>
      </thead>
      <tbody>${itemsHtml}</tbody>
    </table>
    <hr class="hr">
    <table>
      <tr><td>${T.subtotal}</td><td style="text-align:end">${Number(order.subtotal || 0).toFixed(2)}</td></tr>
      <tr><td>${T.delivery}</td><td style="text-align:end">${Number(order.delivery_fee || 0).toFixed(2)}</td></tr>
      <tr class="total-row"><td>${T.grandTotal}</td><td style="text-align:end">${Number(order.total).toFixed(2)} SAR</td></tr>
    </table>
    <div class="muted center" style="margin-top:10px">
      <b>${T.payment}:</b> ${order.payment_method === "cash" ? T.cash : T.card}
    </div>
    <hr class="hr">
    <div class="center muted">${T.thankYou}</div>
  </div>
  <script>window.onload = () => { window.print(); setTimeout(() => window.close(), 500); };</script>
</body>
</html>`;
  // Open a popup with the receipt HTML. We assign via srcdoc and innerHTML,
  // avoiding document.write() which can lead to XSS issues.
  const w = window.open("", "_blank", "width=400,height=700");
  if (!w) {
    alert(lang === "ar" ? "الرجاء السماح بالنوافذ المنبثقة لطباعة الفاتورة" : "Please allow popups to print");
    return;
  }
  try {
    w.document.open();
    w.document.documentElement.innerHTML = html
      .replace(/^<!doctype html>/i, "")
      .replace(/^<html[^>]*>/i, "")
      .replace(/<\/html>$/i, "");
  } finally {
    w.document.close();
  }
}
